import Navbar from "./components/Navbar";
import SignUpPage from "./pages/SignUpPage";
import LoginPage from "./pages/LoginPage";
import ProfilePage from "./pages/ProfilePage";
import ClientPage from "./pages/ClientPage";
import ProposalPage from "./pages/ProposalsPage";
import Chat from "./pages/chat";
import DashboardLayout from "./components/layout/DashboardLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "./store/useAuthStore";
import { useThemeStore } from "./store/useThemeStore";
import { useErpStore } from "./store/useErpStore";
import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import ProjectsPage from "./pages/Project";
import QuestionnairesPage from "./pages/QuestionnairesPage";
import QuestionnaireDetailsPage from "./pages/QuestionnaireDetailsPage";
import PublicQuestionnaire from "./components/questionnaires/PublicQuestionnaire";
import EditQuestionnairePage from "./pages/EditQuestionnairePage";
import InterviewPrepPage from "./pages/InterviewPrepPage";
import SettingsPage from "./pages/SettingsPage";
import BDInvoicesPage from "./pages/BDInvoicesPage";
import BDCommissionsPage from "./pages/BDCommissionsPage";
import BDFilesPage from "./pages/BDFilesPage";
import { usePushNotifications } from "./hooks/usePushNotifications";
import BidsPage from "./pages/BidsPage";
import { useActivityTracker } from "./hooks/useActivityTracker";
import PublicProposalView from "./components/proposals/PublicProposalView";
import ErpConnectionModal from "./components/ErpConnectionModal";
import ProposalBuilderPage from "./pages/ProposalBuilderPage";
import ProposalReviewPage from "./pages/ProposalReviewPage";
import ClientProjectsPage from "./pages/ClientProjectsPage";
import UpworkJobsPage from "./pages/Upworkjobspage";
import FinancePage from "./pages/FinancePage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminPerformancePage from "./pages/AdminPerformancePage";
import AdminClientsPage from "./pages/AdminClientsPage";
import AdminProjectsPage from "./pages/AdminProjectsPage";
import AdminAccountPerformancePage from "./pages/AdminAccountPerformancePage";
import AdminCostManagementPage from "./pages/AdminCostManagementPage";
import AdminBiddingConfigPage from "./pages/AdminBiddingConfigPage";
import DailyReportListPage from "./pages/DailyReportListPage";
import DailyReportDetailPage from "./pages/DailyReportDetailPage";
import PublicDailyReportView from "./pages/PublicDailyReportView";
import PaymentPage from "./pages/PaymentPage";
import IntegrationsSettingsPage from "./pages/IntegrationsSettingsPage";
import AdminActivityCategoriesPage from "./pages/AdminActivityCategoriesPage";
import AdminProjectTypesPage from "./pages/AdminProjectTypesPage";
import ReportsPage from "./pages/ReportsPage";
import SharedReportPage from "./pages/SharedReportPage";
import FreelancerJobRecordsPage from "./pages/FreelancerJobRecordsPage";
import ProjectFullPage from "./pages/ProjectFullPage";
import AdminInvoicesPage from "./pages/AdminInvoicesPage";
import AdminCommissionsPage from "./pages/AdminCommissionsPage";
import AdminTargetBonusPage from "./pages/AdminTargetBonusPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import FloatingActivityTimer from "./components/activities/FloatingActivityTimer";
import BDDashboard from "./pages/bd/BDDashboard";
import ClientPortalPage from "./pages/ClientPortalPage";
import ClientServicesPage from "./pages/ClientServicesPage";
import ClientInvoicesPage from "./pages/ClientInvoicesPage";
import ClientFilesPage from "./pages/ClientFilesPage";
import ClientInboxPage from "./pages/ClientInboxPage";
import ClientOnboardingGate from "./pages/ClientOnboardingGate";
import FilesHubPage from "./pages/FilesHubPage";

import { IS_STAGING } from "./lib/api";

import { isOmniTenantHost, currentHost, applyBranding } from "./lib/tenant";

// Client-facing routes honour the Brand Environment's enabled modules. This is
// the usability layer; the backend enforces the same rule on every request.
const FeatureRoute = ({ feature, children }: { feature: string; children: React.ReactNode }) => {
  const tenant: any = useAuthStore((s) => s.authUser?.tenant);
  if (tenant?.features && tenant.features[feature] === false) return <Navigate to="/portal" replace />;
  return <>{children}</>;
};
// Tab title makes the environment obvious even from the browser's tab strip.
if (IS_STAGING) document.title = "Omni Workspace — STAGING";

// Persistent banner so nobody mistakes the staging portal for the live one.
const StagingBanner = () =>
  IS_STAGING ? (
    <div className="fixed bottom-3 left-3 z-[200] bg-amber-500 text-black text-[11px] font-bold tracking-wide px-3 py-1.5 rounded-full shadow-lg pointer-events-none">
      ⚠ STAGING — data is not live
    </div>
  ) : null;

const App = () => {
  const { authUser, checkAuth, isCheckingAuth } = useAuthStore();
  const { theme }: any = useThemeStore();
  const { isConnected, isChecking, checkConnection } = useErpStore();
  const [showErpModal, setShowErpModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isPublicRoute =
  location.pathname.startsWith("/proposal/public") ||
  location.pathname.startsWith("/questionnaire/public") ||
  // A shared report opens without a session when its link was made public.
  // The server still decides — a restricted or revoked link is refused there —
  // but the route has to be reachable for it to get the chance.
  location.pathname.startsWith("/reports/shared/") ||
  location.pathname.startsWith("/pay/");

  const isFinanceUser = authUser?.role === "Finance User";
  const isClient = authUser?.role === "Client";

  // Multi-brand: the client's tenant identity (colors, title, favicon) is
  // applied the moment we know who they are — everywhere they're signed in.
  useEffect(() => {
    if (isClient && authUser?.tenant) applyBranding(authUser.tenant as any);
  }, [isClient, authUser?.tenant]);

  // Tenant boundary (UI side; the API enforces it too): a client on a tenant
  // subdomain that isn't theirs is sent to their own branded environment.
  useEffect(() => {
    if (isClient && authUser?.tenant?.subdomain && isOmniTenantHost() && currentHost() !== authUser.tenant.subdomain) {
      window.location.href = `https://${authUser.tenant.subdomain}`;
    }
  }, [isClient, authUser?.tenant?.subdomain]);

  // Team members work on the CENTRAL omni portal — a brand subdomain is the
  // clients' branded environment, so internal roles are sent back to central.
  useEffect(() => {
    if (authUser && !isClient && isOmniTenantHost()) {
      window.location.href = "https://omniworkspace.app";
    }
  }, [authUser, isClient]);

  useActivityTracker();


  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (authUser && !isFinanceUser) {

      checkConnection();
    }
  }, [authUser, isFinanceUser, checkConnection]);

    useEffect(() => {
    if (authUser && !isChecking) {

      if (!isConnected) {

        setTimeout(() => {
          setShowErpModal(true);
        }, 1000);
      } else {

        setShowErpModal(false);
      }
    }
  }, [authUser, isConnected, isChecking]);
  usePushNotifications(authUser);

  // Finance User is confined to the Projects + Invoices financial review modules
  // (and their own profile). Any other in-app route bounces back to /projects.
  useEffect(() => {
    if (!isFinanceUser || isPublicRoute) return;
    const allowed = ["/projects", "/profile", "/admin/invoices", "/admin/commissions"];
    const path = location.pathname;
    const ok = allowed.some((p) => path === p || path.startsWith(p + "/"));
    if (!ok && path !== "/login" && path !== "/signup") {
      navigate("/projects", { replace: true });
    }
  }, [isFinanceUser, isPublicRoute, location.pathname, navigate]);

  // Client accounts live entirely inside the Client Portal. Any staff route
  // bounces back to /portal.
  useEffect(() => {
    if (!isClient || isPublicRoute) return;
    const path = location.pathname;
    const ok = path === "/portal" || path.startsWith("/portal/") || path === "/login" || path === "/signup";
    if (!ok) navigate("/portal", { replace: true });
  }, [isClient, isPublicRoute, location.pathname, navigate]);

  // First-login onboarding: a freshly-provisioned client must set a password and
  // confirm their profile before the portal opens.
  if (authUser && isClient && authUser.mustChangePassword && !isPublicRoute) {
    return (
      <div data-theme={theme} className="min-h-screen">
        <StagingBanner />
        <ClientOnboardingGate />
        <Toaster position="top-center" />
      </div>
    );
  }

  return (
    <div data-theme={theme} className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-slate-50">
      <StagingBanner />
      {authUser && !isPublicRoute && authUser.role !== "Business Developer" && !isClient && <Navbar />}
      {/* Personal activity tracker is only for the BD tracking their own work —
          admins/managers oversee activities from the Activities page, not here. */}
      {authUser && !isPublicRoute && authUser.role === "Business Developer" && <FloatingActivityTimer />}

      <Routes>
        <Route path="/questionnaire/public/:token" element={<PublicQuestionnaire />} />
        {/* A shared report opens without a session when its link is public.
            The server decides; this route just has to be reachable. */}
        <Route path="/reports/shared/:token" element={<SharedReportPage />} />
        <Route path="/proposal/public/:token" element={<PublicProposalView />} />
        <Route path="/reports/public/:token" element={<PublicDailyReportView />} />
        <Route path="/pay/:token" element={<PaymentPage />} />

        <Route
          path="/"
          element={
            authUser ? (
              <Navigate
                to={
                  ["Admin", "Supervisor", "Sales Director"].includes(authUser.role as any)
                    ? "/admin/dashboard"
                    : authUser.role === "Business Developer"
                    ? "/bd/dashboard"
                    : authUser.role === "Finance User"
                    ? "/projects"
                    : authUser.role === "Client"
                    ? "/portal"
                    : "/chat"
                }
                replace
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Client Portal — standalone (its own chrome), Client role only */}
        <Route
          path="/portal"
          element={
            <ProtectedRoute roles={["Client"]}>
              <ClientPortalPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/services"
          element={
            <ProtectedRoute roles={["Client"]}>
              <FeatureRoute feature="services">
                <ClientServicesPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/projects"
          element={
            <ProtectedRoute roles={["Client"]}>
              <FeatureRoute feature="projects">
                <ClientProjectsPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/invoices"
          element={
            <ProtectedRoute roles={["Client"]}>
              <FeatureRoute feature="invoices">
                <ClientInvoicesPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/files"
          element={
            <ProtectedRoute roles={["Client"]}>
              <FeatureRoute feature="files">
                <ClientFilesPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal/inbox"
          element={
            <ProtectedRoute roles={["Client"]}>
              <FeatureRoute feature="inbox">
                <ClientInboxPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/performance" element={<AdminPerformancePage />} />
          {/* Financials removed from the Admin portal (4-Sep decision) — old links land on the dashboard. */}
          <Route path="/admin/financials" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/clients" element={<AdminClientsPage />} />
          <Route path="/admin/projects" element={<AdminProjectsPage />} />
          <Route path="/bd/dashboard" element={<BDDashboard />} />
          <Route path="/bd/invoices" element={<BDInvoicesPage />} />
          <Route path="/bd/commissions" element={<BDCommissionsPage />} />
          <Route path="/bd/files" element={<BDFilesPage />} />
          <Route path="/files" element={<FilesHubPage />} />
          <Route path="/reports/daily" element={<DailyReportListPage />} />
          <Route path="/reports/daily/:id" element={<DailyReportDetailPage />} />
          <Route path="/settings/integrations" element={<IntegrationsSettingsPage />} />
          <Route path="/settings" element={<ProtectedRoute roles={["Admin", "Sales Director"]}><SettingsPage /></ProtectedRoute>} />
          <Route path="/admin/activity-categories" element={<AdminActivityCategoriesPage />} />
          <Route path="/admin/bd-target-bonus" element={<AdminTargetBonusPage />} />
          <Route path="/admin/project-types" element={<AdminProjectTypesPage />} />
          {/* Reports & Analytics — the management intelligence layer. Scope is
              resolved server-side from the caller, so a BD opening this sees
              their own figures. */}
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/admin/invoices" element={<AdminInvoicesPage />} />
          <Route path="/admin/commissions" element={<AdminCommissionsPage />} />
          <Route path="/activities" element={<ActivitiesPage />} />
          <Route path="/interview-prep" element={<InterviewPrepPage />} />
          <Route
            path="/admin/account-performance"
            element={<AdminAccountPerformancePage />}
          />
          <Route
            path="/admin/cost-management"
            element={<AdminCostManagementPage />}
          />
          <Route path="/admin/bidding-config" element={<AdminBiddingConfigPage />} />
          {/* An account's completed work — the evidence the AI matches new jobs against. */}
          <Route path="/freelancer-job-records" element={<FreelancerJobRecordsPage />} />
          {/* The complete record of one project — every field, both milestone
              sets, documents, questionnaire answers, and the exact ERP payload. */}
          <Route path="/projects/:id" element={<ProjectFullPage />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/clients" element={<ClientPage />} />
          <Route path="/questionnaires" element={<QuestionnairesPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/proposal-builder" element={<ProposalBuilderPage />} />
          <Route path="/proposals" element={<ProposalPage/>} />
          <Route path="/proposals/:id/review" element={<ProposalReviewPage />} />
          <Route path="/questionnaires/:id" element={<QuestionnaireDetailsPage />} />
          <Route path="/questionnaires/:id/edit" element={<EditQuestionnairePage />} />
          <Route path="/bids" element={<BidsPage />} />
          <Route path="/upwork-jobs" element={<UpworkJobsPage />} />
          <Route path="/finance" element={<FinancePage />} />
        </Route>

        <Route
          path="/login"
          element={!authUser ? <LoginPage /> : <Navigate to="/" replace />}
        />
        <Route
          path="/signup"
          element={!authUser ? <SignUpPage /> : <Navigate to="/" replace />}
        />
      </Routes>

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(12px)",
            border: `1px solid rgba(36, 98, 178, 0.1)`,
            boxShadow: "0 8px 32px rgba(36, 98, 178, 0.1)",
            borderRadius: "12px",
            padding: "12px 16px",
            fontWeight: "500",
            color: "#1f2937",
          },
          success: {
            iconTheme: { primary: "#48ff31", secondary: "#fff" },
          },
          error: {
            iconTheme: { primary: "#ef4444", secondary: "#fff" },
          },
        }}
      />
    </div>
  );
};

export default App;



